import { BrowserSessionApiClient } from './browserSessionApiClient';

export const packageStatuses = ['ready', 'in_progress', 'waiting', 'complete'] as const;
export type PackageStatus = typeof packageStatuses[number];
export type LinkedTask = { id?: number; title?: string; owner?: string; status?: string; dueDate?: string };
export type ControlProject = { description?: string | null; projectType?: string | null; linkedTasks?: LinkedTask[] | null; projectId: string; name: string; projectName?: string | null; projectNumber?: string | null; siteAddress?: string | null; version: number; committedDate: string | null };
export function projectCardTitle(project?: ControlProject) {
  return project?.projectName || project?.siteAddress || (project?.projectName === undefined ? project?.name : null) || 'Unnamed project';
}
export type WorkPackage = {
  version: number;
  id: string;
  projectId: string;
  title: string;
  resourceId: string | null;
  remainingHours: number | null;
  status: PackageStatus;
  dependencies: string[];
  priority: number;
};
export type ResourceCapacity = { version: number; resourceId: string; date: string; hours: number; confirmed: boolean };
export type CapacityDay = {
  resourceId: string; date: string; availableHours: number | null;
  allocatedHours: number; freeHours: number | null;
  source?: 'dated' | 'weekly' | 'external';
};
export type DeliveryRisk = {
  projectId: string; committedDate: string | null; forecastDate: string | null;
  slackHours?: number | null; shortfallHours: number | null; risk: 'critical' | 'high' | 'medium' | 'low' | 'unknown'; reasons: string[];
};
export type WipLimit = { resourceId: string; maxProjects: number; maxHours: number; version: number };
type FinishProject = {
  projectId: string; remainingHours: number | null; todayHours: number; packageIds: string[];
};
type StartCandidate = FinishProject & {
  alreadyActive: boolean; projectedProjects: number; projectedHours: number | null;
  eligible: boolean; reasons: string[];
};
export type ResourceWip = {
  resourceId: string; maxProjects: number | null; maxHours: number | null;
  activeProjects: number; activeHours: number | null; finishFirst: FinishProject[]; candidates: StartCandidate[];
};
export type TurnaroundType = { typeName: string; defaultDays: number; version: number };
export type ProjectTiming = {
  projectId: string; typeName: string | null; receivedDate: string; readyDate: string | null;
  overrideDays: number | null; deliveredDate: string | null; version: number;
};
export type CheckpointUpdate = { projectId: string; checkpointKey: string; note: string; updatedDate: string };
export type TimingReminder = {
  kind: 'inputs' | 'midpoint' | 'target' | 'overdue' | 'risk'; dueDate: string; message: string; checkpointKey: string | null;
};
export type TurnaroundRow = {
  sourceType?: string | null;
  sourceDefaultTypeName?: string | null;
  projectId: string; workingDays: number | null; receiptAgeDays: number | null; targetDate: string | null;
  midpointDate: string | null; reminders: TimingReminder[];
};
export type ControlSnapshot = {
  today: string;
  through: string;
  canEdit: boolean;
  projects: ControlProject[];
  packages: WorkPackage[];
  capacity: ResourceCapacity[];
  resourceIdentities?: Record<string,string>;
  wipLimits?: WipLimit[];
  wipLimitsAvailable?: boolean;
  wip?: ResourceWip[];
  turnaroundAvailable?: boolean;
  turnaroundTypes?: TurnaroundType[];
  timingProfiles?: ProjectTiming[];
  checkpointUpdates?: CheckpointUpdate[];
  turnaround?: TurnaroundRow[];
  plan: {
    days: CapacityDay[];
    allocations: { packageId: string; resourceId: string; date: string; hours: number }[];
    projects: DeliveryRisk[];
    priorities: string[];
  };
};
export function planningResourceId(snapshot: ControlSnapshot, raw: string | null) {
  return raw === null ? null : snapshot.resourceIdentities?.[raw] ?? raw;
}
export type PlanningChange =
  | ({ kind: 'package' } & WorkPackage)
  | ({ kind: 'capacity' } & ResourceCapacity)
  | ({ kind: 'wip_limit' } & WipLimit)
  | ({ kind: 'turnaround_type' } & TurnaroundType)
  | ({ kind: 'project_timing' } & ProjectTiming)
  | { kind: 'checkpoint_update'; projectId: string; checkpointKey: string; note: string; version: number }
  | { kind: 'commitment'; version: number; projectId: string; committedDate: string | null };

const client = new BrowserSessionApiClient('/api');
const path = '/api/v1/project-control-centre';

export async function loadControlCentre(): Promise<ControlSnapshot> {
  const result = await client.request<{ data: ControlSnapshot }>(path);
  return result.data;
}

export async function savePlanningChange(body: PlanningChange): Promise<void> {
  await client.request(path, { method: 'PUT', body });
}
